import React from 'react';
import { X, Database, ArrowRight, CheckCircle, Loader } from 'lucide-react';

const WelcomeModal = ({ isOpen, onClose, onStartSetup, onSkip, hasKcirodeoFeatures, counts, userEmail, isLoading }) => {
  if (!isOpen) return null;

  const { entriesCount = 0, resultsCount = 0, membershipsCount = 0, totalCount = 0 } = counts || {};

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]" style={{ zIndex: 9999 }}>
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold">Welcome to MyTrailer!</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="p-6">
          {isLoading ? (
            <div className="text-center py-8">
              <Loader className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
              <p className="text-gray-600">Checking for available data...</p>
            </div>
          ) : (
            <>
              <div className="text-center mb-6">
                <p className="text-black-600 text-lg">
                  Thank you for joining MyTrailer! If you have any questions or need a hand getting started, please contact us at <b>support@kcisoftware.net</b> or visit the help center in the menu.
                </p>
                <p className="text-black-600 text-lg">
                  {hasKcirodeoFeatures 
                    ? "If your association is a member of KCIRodeo, or if you enter their shows online, then you likely have a user account with that association.  Below will get you started with importing your data."
                    : ""
                  }
                </p>
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Database className="w-12 h-12 text-blue-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  {hasKcirodeoFeatures ? "Let's set up your KCIRodeo integration!" : "Welcome to your rodeo management system!"}
                </h3>
                <p className="text-gray-600 text-lg">
                  {hasKcirodeoFeatures 
                    ? "Import your competition data and get started with MyTrailer in just a few steps."
                    : "You're all set to start managing your rodeo activities and tracking your performance."
                  }
                </p>
              </div>

              {/* Show counts if data found */}
              {totalCount > 0 && userEmail && (
                <>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                    <h4 className="font-semibold text-blue-900 mb-2 text-center">
                      We found your data!
                    </h4>
                    <p className="text-gray-700 text-sm mb-4 text-center">
                      We found KCIRodeo data associated with <strong>{userEmail}</strong>
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      {entriesCount > 0 && (
                        <div className="bg-white rounded-lg p-4 text-center">
                          <div className="text-3xl font-bold text-blue-600">{entriesCount}</div>
                          <div className="text-sm text-gray-600 mt-1">
                            {entriesCount === 1 ? 'Entry' : 'Entries'}
                          </div>
                        </div>
                      )}
                      {resultsCount > 0 && (
                        <div className="bg-white rounded-lg p-4 text-center">
                          <div className="text-3xl font-bold text-blue-600">{resultsCount}</div>
                          <div className="text-sm text-gray-600 mt-1">
                            {resultsCount === 1 ? 'Result' : 'Results'}
                          </div>
                        </div>
                      )}
                      {membershipsCount > 0 && (
                        <div className="bg-white rounded-lg p-4 text-center">
                          <div className="text-3xl font-bold text-blue-600">{membershipsCount}</div>
                          <div className="text-sm text-gray-600 mt-1">
                            {membershipsCount === 1 ? 'Membership' : 'Memberships'}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
                    <h4 className="font-semibold text-gray-900 mb-2">What happens next?</h4>
                    <ul className="text-sm text-gray-700 space-y-2">
                      <li className="flex items-start">
                        <CheckCircle className="w-4 h-4 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span>We'll help you approve your email address</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-4 h-4 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span>Import all your entries, results, and memberships</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-4 h-4 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span>Set up automatic syncing for future updates</span>
                      </li>
                    </ul>
                  </div>
                </>
              )}

              {hasKcirodeoFeatures && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                  <h4 className="font-semibold text-blue-900 mb-3">What you can do with KCIRodeo integration:</h4>
                  <ul className="text-sm text-blue-800 space-y-2">
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-blue-600 mr-2 flex-shrink-0" />
                      Import your competition entries automatically
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-blue-600 mr-2 flex-shrink-0" />
                      Sync your results and performance data
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-blue-600 mr-2 flex-shrink-0" />
                      Manage your association memberships
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-blue-600 mr-2 flex-shrink-0" />
                      Create calendar events for your competitions
                    </li>
                  </ul>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                {hasKcirodeoFeatures && (
                  <button
                    onClick={onStartSetup}
                    className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 font-medium"
                  >
                    {totalCount > 0 ? "Yes, Import My Data" : "Start KCIRodeo Setup"}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                )}
                
                <button
                  onClick={onSkip}
                  className="bg-gray-100 text-gray-700 px-8 py-3 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                >
                  {hasKcirodeoFeatures ? "I'll do this later" : "Get Started"}
                </button>
              </div>

              <div className="text-center mt-4">
                <p className="text-sm text-gray-500">
                  {hasKcirodeoFeatures 
                    ? totalCount > 0 
                      ? "You can always import your data later by going to Settings > KCI Rodeo Sync and clicking 'Sync Now'."
                      : "You can always set up KCIRodeo integration later in Settings."
                    : "You can explore all features from the main navigation."
                  }
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default WelcomeModal;
